import React, { useState, useEffect } from 'react';
import {
  Typography, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box,
  Container, Stack, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Snackbar, Alert, FormControl, InputLabel, Select
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import Footer from '../Materials/Footer';
import BurgerBar from '../Materials/BurgerBar';
import { useAuth } from '../../context/AuthContext';

export default function AdminPage() {
 const { user, loading, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  

  const [openDrawer, setOpenDrawer] = useState(false);
  const [users, setUsers] = useState([]);
  const [openFirstDeleteDialog, setOpenFirstDeleteDialog] = useState(false);
  const [openSecondDeleteDialog, setOpenSecondDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userNameToDelete, setUserNameToDelete] = useState('');
  const [inputNameForDeletion, setInputNameForDeletion] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [permissions, setPermissions] = useState([]);

  // Paginação
  const [page, setPage] = useState(1);
  const usersPerPage = 10;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    entity_id: null, 
    user_permission_id: null, 
    country: '',
    contact: '',
    description: ''
  }); 

  const dialogStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '600px',
    bgcolor: 'background.paper',
    p: 4,
    borderRadius: 2
  };


// Agora os useEffect vêm ANTES do retorno condicional
useEffect(() => {
  fetchUsers();
  fetchPermissions();
}, []);

useEffect(() => {
  if (!loading) {
    if (!user || !isAdmin()) {
      alert("Access denied: Admins only");
      logout();
      navigate('/', { replace: true });
    }
  }
}, [user, loading, navigate, logout, isAdmin]);

if (loading || !user) {
  return null;
}


  const generateRandomPassword = () => {
    return Math.random().toString(36).slice(-8);
  };

  const fetchPermissions = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/user-permissions');
      if (!response.ok) throw new Error('Failed to fetch permissions');
      const data = await response.json();
      setPermissions(data);
    } catch (error) {
      showSnackbar('Error fetching permissions: ' + error.message, 'error');
    }
  };

  // Obter nome da permissão pelo ID
  const getPermissionName = (id) => {
    const permission = permissions.find(p => p.id === id);
    return permission ? permission.type : id;
  };

  const calculateTimeAgo = (timestamp) => {
    if (!timestamp) return 'N.A';
    
    const now = new Date();
    const lastOnline = new Date(timestamp);
    const diffInSeconds = Math.floor((now - lastOnline) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/users');
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      showSnackbar('Error fetching users: ' + error.message, 'error');
    }
  };

  // Resto das funções permanecem iguais...
  const handleOpenDeleteDialog = (userId, userName) => {
    setUserToDelete(userId);
    setUserNameToDelete(userName);
    setInputNameForDeletion('');
    setOpenFirstDeleteDialog(true);
  };

  const handleCloseFirstDeleteDialog = () => {
    setOpenFirstDeleteDialog(false);
  };

  const handleCloseSecondDeleteDialog = () => {
    setOpenSecondDeleteDialog(false);
    setUserToDelete(null);
    setUserNameToDelete('');
    setInputNameForDeletion('');
  };

  const handleConfirmFirstDelete = () => {
    setOpenFirstDeleteDialog(false);
    setOpenSecondDeleteDialog(true);
  };

  const handleInputChange = (event) => {
    setInputNameForDeletion(event.target.value);
  };

  const handleDeleteUser = async () => {
    if (inputNameForDeletion !== userNameToDelete) {
      showSnackbar('User name does not match. Please try again.', 'error');
      return;
    }

    try {
      const response = await fetch(`https://api.hub4food.colab4food.com/api/users/${userToDelete}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      setUsers(users.filter(user => user.id !== userToDelete));
      handleCloseSecondDeleteDialog();
      showSnackbar('User deleted successfully', 'success');
    } catch (error) {
      showSnackbar('Error deleting user: ' + error.message, 'error');
    }
  };

  const handleOpenEditDialog = (user) => {
    setCurrentUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      entity_id: user.entity_id,
      user_permission_id: user.user_permission_id,
      country: user.country,
      contact: user.contact,
      description: user.description
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setCurrentUser(null);
    setFormData({
      name: '',
      email: '',
      entity_id: '',
      user_permission_id: '',
      country: '',
      contact: '',
      description: ''
    });
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setFormData({
      name: '',
      email: '',
      entity_id: null,
      user_permission_id: null,
      country: '',
      contact: '',
      description: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateUser = async () => {
    try {
      const response = await fetch(`https://api.hub4food.colab4food.com/api/users/${currentUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      const updatedUser = await response.json();

setUsers(users.map(user =>
  user.id === updatedUser.id
    ? {
        ...user,
        ...updatedUser,
        last_time_online: updatedUser.last_time_online || user.last_time_online
      }
    : user
));
      handleCloseEditDialog();
      showSnackbar('User updated successfully', 'success');
    } catch (error) {
      showSnackbar('Error updating user: ' + error.message, 'error');
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!formData.user_permission_id) {
        showSnackbar('Por favor, selecione um nível de permissão', 'error');
        return;
      }
  
      if (permissions.length === 0) {
        showSnackbar('Aguarde enquanto carregamos as permissões', 'error');
        return;
      }
  
      const permissionExists = permissions.some(p => p.id === formData.user_permission_id);
      if (!permissionExists) {
        showSnackbar('Permissão selecionada é inválida', 'error');
        return;
      }
  
      const userData = {
        ...formData,
        password: generateRandomPassword(),
        user_permission_id: formData.user_permission_id 
      };
  
      console.log('Dados sendo enviados:', userData); 
      const response = await fetch('https://api.hub4food.colab4food.com/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }
  
      const newUser = await response.json();
      setUsers([...users, newUser]);
      handleCloseCreateDialog();
      showSnackbar('User created successfully', 'success');
    } catch (error) {
      showSnackbar('Error creating user: ' + error.message, 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Paginação dos utilizadores
  const totalPages = Math.ceil(users.length / usersPerPage);
  const startIndex = (page - 1) * usersPerPage;
  const paginatedUsers = users.slice(startIndex, startIndex + usersPerPage);

  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <BurgerBar />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ mt: 2, mb: 2 }}
            onClick={handleOpenCreateDialog}
          >
            New User
          </Button>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Permission</TableCell>
                  <TableCell align="center">Last Time Online</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell align="center">{user.name}</TableCell>
                    <TableCell align="center">{user.email}</TableCell>
                    <TableCell align="center">
                      {user.user_permission_id ? getPermissionName(user.user_permission_id) : 'N.A'}
                    </TableCell>
                    <TableCell align="center">
                      {calculateTimeAgo(user.last_time_online)}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenEditDialog(user)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleOpenDeleteDialog(user.id, user.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>
      </Container>

      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth PaperProps={{ sx: dialogStyle }}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent
          sx={{
            px: 4, 
            py: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            component="form"
            sx={{
              width: '100%',
              maxWidth: 500,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mx: 'auto', 
            }}
          >
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
            />
            <FormControl fullWidth>
              <InputLabel>Permission Level</InputLabel>
              <Select
                name="user_permission_id"
                value={formData.user_permission_id}
                label="Permission Level"
                onChange={handleFormChange}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission.id} value={permission.id}>
                    {permission.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              label="Contact"
              name="contact"
              value={formData.contact}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="error">Cancel</Button>
          <Button onClick={handleUpdateUser} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogStyle }}
      >
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent
          sx={{
            px: 4,
            py: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            component="form"
            sx={{
              width: '100%',
              maxWidth: 500,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mx: 'auto',
            }}
          >
            <TextField
              fullWidth
              required
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              required
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              label="Entity ID"
              name="entity_id"
              value={formData.entity_id}
              onChange={handleFormChange}
            />
            <FormControl fullWidth required error={!formData.user_permission_id}>
              <InputLabel>Permission Level *</InputLabel>
              <Select
                name="user_permission_id"
                value={formData.user_permission_id || ''}
                label="Permission Level *"
                onChange={handleFormChange}
                required
              >
                <MenuItem disabled value="">
                  <em>Select the permission</em>
                </MenuItem>
                {permissions.map((permission) => (
                  <MenuItem key={permission.id} value={permission.id}>
                    {permission.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              label="Contact"
              name="contact"
              value={formData.contact}
              onChange={handleFormChange}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} color="error">Cancel</Button>
          <Button onClick={handleCreateUser} color="primary">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialogs */}
      <Dialog open={openFirstDeleteDialog} onClose={handleCloseFirstDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the user "{userNameToDelete}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFirstDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmFirstDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSecondDeleteDialog} onClose={handleCloseSecondDeleteDialog}>
        <DialogTitle>Final Confirmation</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            To delete the user "{userNameToDelete}", type the user's name below:
          </Typography>
          <TextField
            fullWidth
            value={inputNameForDeletion}
            onChange={handleInputChange}
            label="Type user name"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSecondDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box>
        <Footer />
      </Box>
    </Box>
  );
}